import { useEffect, useMemo, useState } from "react";
import { img, srcSet, altOf, nameOf } from "@/lib/media";
import { useLang } from "@/lib/i18n";
import { Lightbox } from "./Lightbox";
import { Reveal } from "./Reveal";
import { GalleryVideo } from "./GalleryVideo";

const colsFor = (w) => (w >= 900 ? 3 : 2);

const useColumns = () => {
  const [n, setN] = useState(() => colsFor(window.innerWidth));
  useEffect(() => {
    const onResize = () => setN(colsFor(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return n;
};

const balance = (items, n) => {
  const cols = Array.from({ length: n }, () => ({ h: 0, items: [] }));
  items.forEach((p, i) => {
    const c = cols.reduce((a, b) => (b.h < a.h ? b : a));
    c.items.push({ p, i });
    c.h += p.height / p.width;
  });
  return cols;
};

export const Gallery = ({ themes, photos, filter, setFilter }) => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(-1);
  const n = useColumns();
  const shown = useMemo(() => photos.filter((p) => p.theme === filter), [photos, filter]);
  const cols = useMemo(() => balance(shown, n), [shown, n]);
  const chips = themes.map((th) => ({ id: th.id, label: nameOf(th, lang) }));
  return (
    <section id="gallery" data-testid="gallery-section">
      <div className="wrap">
        <Reveal><h1 className="gallery-h">{t.gallery.h}</h1></Reveal>
        <div className="chips" role="group" aria-label={t.gallery.filter}>
          {chips.map((c) => (
            <button key={c.id} type="button" className="chip" aria-pressed={filter === c.id}
              onClick={() => { setOpen(-1); setFilter(c.id); }} data-testid={`gallery-chip-${c.id}`}>{c.label}</button>
          ))}
        </div>
        <div className="masonry" data-testid="gallery-grid">
          {cols.map((col, ci) => (
            <div className="mcol" key={ci}>
              {col.items.map(({ p, i }) => (
                <button key={p.file} type="button" className="item" aria-label={`${t.gallery.enlarge}: ${altOf(p, lang)}`}
                  onClick={() => setOpen(i)} data-testid={`gallery-item-${p.file.split("/").pop().split(".")[0]}`}>
                  {p.type === "video" ? <GalleryVideo item={p} alt={altOf(p, lang)} /> : (
                  <img loading="lazy" decoding="async" alt={altOf(p, lang)} width={p.width} height={p.height}
                    style={{ aspectRatio: `${p.width} / ${p.height}` }}
                    src={img(p.file, p.low_resolution ? 480 : 960)} srcSet={srcSet(p.file, p.low_resolution)}
                    sizes="(max-width:900px) 50vw, 380px" />)}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
      {open >= 0 && shown[open] && (
        <Lightbox items={shown} index={open} setIndex={setOpen} onClose={() => setOpen(-1)} />
      )}
    </section>
  );
};
